import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DrupalRESTService } from './drupal-rest.service';
import moment from 'moment';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class CalendarDataService {

  constructor(
    private _drupalRESTService: DrupalRESTService
  ) { }

  /**
   * Fetches upcoming events for a specific event
   * @param currentEventId The ID of the current event
   * @param eventDate The date of the event
   * @param studentAccountId Optional student account ID for filtering
   * @returns Observable with upcoming events data
   */
  fetchNextEvents(currentEventId: string, eventDate: Date | string, studentAccountId?: string): Observable<any> {
    if (!currentEventId || !eventDate) {
      LoggerService.error('[CalendarDataService] Missing required parameters for fetchNextEvents:', { currentEventId, eventDate });
      throw new Error('Missing required parameters for fetchNextEvents');
    }

    // Format the date
    const formattedDate = moment(eventDate).format('YYYY-MM-DD');
    
    // Build endpoint
    let endpoint = `/api/dayview/calendar/next_events?current_event_id=${currentEventId}&date=${formattedDate}`;
    
    // Add student_account_id if available
    if (studentAccountId) {
      endpoint += `&student_account_id=${studentAccountId}`;
    }
    
    LoggerService.log('[CalendarDataService] Fetching next events from:', endpoint);
    
    return this._drupalRESTService.httpGET(endpoint);
  }

  /**
   * Fetches calendar events for a specific date range
   * @param startDate Start date for the range
   * @param endDate End date for the range
   * @param calendarDate The calendar date for context
   * @returns Observable with calendar events
   */
  fetchCalendarEvents(startDate: Date | string, endDate: Date | string, calendarDate?: Date | string): Observable<any> {
    const startOfDay = moment(startDate).startOf('day').toISOString();
    const endOfDay = moment(endDate).endOf('day').toISOString();
    
    let endpoint = "/api/dayview/calendar/events?";
    endpoint = endpoint + "start=" + startOfDay;
    endpoint = endpoint + "&end=" + endOfDay;
    
    if (calendarDate) {
      endpoint = endpoint + "&calendar_date=" + moment(calendarDate).toISOString();
    }
    
    LoggerService.log('[CalendarDataService] Fetching calendar events from:', endpoint);
    
    return this._drupalRESTService.httpGET(endpoint);
  }

  /**
   * Fetches a single event's data
   * TODO: This should be implemented as a dedicated endpoint instead of fetching all events
   * @param entityId The entity ID of the event
   * @param eventDate The date of the event
   * @returns Observable with the specific event data
   */
  fetchSingleEvent(entityId: string, eventDate: Date | string): Observable<any> {
    // For now, we fetch all events for the day and filter
    // TODO: Create dedicated endpoint for single event
    return new Observable(observer => {
      this.fetchCalendarEvents(eventDate, eventDate, eventDate).subscribe(
        (response: any) => {
          const events = response?.events || response || [];
          
          // Find the matching event
          const matchingEvent = events.find((e: any) => {
            const eventEntityId = e.extendedProps?.entityId || 
                                 e.entityId || 
                                 e.id ||
                                 e._def?.extendedProps?.entityId;
            
            return eventEntityId == entityId || eventEntityId === entityId.toString();
          });
          
          if (matchingEvent) {
            observer.next(matchingEvent);
          } else {
            observer.error(`Event with entityId ${entityId} not found`);
          }
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }
}